// src/pages/Appli/AppliAdmin/ListingClients/CartoClients.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

import L from "leaflet";

import {
  FaBuilding,
  FaEnvelope,
  FaExclamationTriangle,
  FaFilter,
  FaGlobe,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSearch,
  FaStar,
  FaSync,
  FaTimes,
} from "react-icons/fa";

import "leaflet/dist/leaflet.css";

import { getAllAssociations } from "../../../../services/associationsServices";

import useScrollToTop from "../../../../hooks/useScrollToTop";

import AssociationStatusBadge from "./ui/AssociationStatusBadge";

import {
  ASSOCIATION_STATUS_INDEX,
  ASSOCIATION_STATUS_VALUES,
  getAssociationStatusMeta,
} from "./data/associations.data";

import {
  formatAssociationDate,
  getAssociationApiError,
  getAssociationSearchText,
  getReminderState,
  normalizeSearchText,
  splitDisciplines,
} from "./utils/association.utils";

const DEFAULT_CENTER = [46.6, 2.5];
const DEFAULT_ZOOM = 6;
const fallbackLogo = "/images/no-image.jpg";

const INITIAL_FILTERS = {
  search: "",
  status: "",
  product: "",
  discipline: "",
  favoriteOnly: false,
};

const SELECT_CLASS = [
  "w-full rounded-xl border",
  "border-slate-200 dark:border-slate-700",
  "bg-white dark:bg-slate-900",
  "px-3 py-2.5",
  "text-sm text-slate-800 dark:text-slate-100",
  "outline-none transition",
  "focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10",
].join(" ");

const markerIconCache = new Map();

function hasValidCoordinates(association) {
  const rawLatitude = association.latitude;
  const rawLongitude = association.longitude;

  if (
    rawLatitude === null ||
    rawLatitude === undefined ||
    rawLongitude === null ||
    rawLongitude === undefined
  ) {
    return false;
  }

  const latitudeText = String(rawLatitude).trim();
  const longitudeText = String(rawLongitude).trim();

  if (!latitudeText || !longitudeText) {
    return false;
  }

  const latitude = Number(latitudeText.replace(",", "."));

  const longitude = Number(longitudeText.replace(",", "."));

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return false;
  }

  // Coordonnées généralement enregistrées par erreur
  if (latitude === 0 && longitude === 0) {
    return false;
  }

  return true;
}

function normalizePhone(value) {
  return String(value || "").replace(/[^\d+]/g, "");
}

function normalizeWebsite(value) {
  const website = String(value || "").trim();

  if (!website) return "";

  if (/^https?:\/\//i.test(website)) {
    return website;
  }

  return `https://${website}`;
}

function getStatusMarkerIcon(status) {
  const meta = getAssociationStatusMeta(status);
  const cacheKey = meta.chartColor;

  if (markerIconCache.has(cacheKey)) {
    return markerIconCache.get(cacheKey);
  }

  const icon = L.divIcon({
    className: "association-marker-wrapper",

    html: `
      <span
        class="association-map-marker"
        style="--association-marker-color: ${meta.chartColor}"
      >
        <span class="association-map-marker-dot"></span>
      </span>
    `,

    iconSize: [36, 44],
    iconAnchor: [18, 42],
    popupAnchor: [0, -38],
  });

  markerIconCache.set(cacheKey, icon);

  return icon;
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={SELECT_CLASS}
      >
        {children}
      </select>
    </label>
  );
}

function MapViewportController({ associations, selectedAssociationId }) {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();

      if (!associations.length) {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);

        return;
      }

      if (associations.length === 1) {
        const association = associations[0];

        map.setView(
          [Number(association.latitude), Number(association.longitude)],
          11,
        );

        return;
      }

      const bounds = L.latLngBounds(
        associations.map((association) => [
          Number(association.latitude),
          Number(association.longitude),
        ]),
      );

      map.fitBounds(bounds, {
        padding: [40, 40],
        maxZoom: 11,
      });
    }, 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, [associations, map]);

  useEffect(() => {
    if (!selectedAssociationId) return;

    const selectedAssociation = associations.find(
      (association) => association.id === selectedAssociationId,
    );

    if (!selectedAssociation) return;

    map.flyTo(
      [
        Number(selectedAssociation.latitude),
        Number(selectedAssociation.longitude),
      ],
      Math.max(map.getZoom(), 10),
      {
        animate: true,
        duration: 0.7,
      },
    );
  }, [associations, map, selectedAssociationId]);

  return null;
}

function AssociationPopup({ association }) {
  const phone = normalizePhone(association.tel_asso);

  const website = normalizeWebsite(association.siteweb_asso);

  const googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(
    `${association.latitude},${association.longitude}`,
  )}`;

  return (
    <div className="min-w-[245px] max-w-[290px]">
      <div className="flex items-start gap-3">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white p-1">
          <img
            src={association.logo_asso || fallbackLogo}
            alt={`Logo de ${association.nom_asso || "l’association"}`}
            className="h-full w-full object-contain"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = fallbackLogo;
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-5 text-slate-900">
            {association.nom_asso || "—"}
          </div>

          <div className="mt-1">
            <AssociationStatusBadge status={association.statut_asso} />
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
        {association.produit_asso ? (
          <div className="text-xs text-slate-600">
            <span className="font-semibold">Produit :</span>{" "}
            {association.produit_asso}
          </div>
        ) : null}

        {association.discipline_asso ? (
          <div className="text-xs text-slate-600">
            <span className="font-semibold">Discipline :</span>{" "}
            {association.discipline_asso}
          </div>
        ) : null}

        {association.adresse_asso ? (
          <div className="flex items-start gap-2 text-xs text-slate-600">
            <FaMapMarkerAlt className="mt-0.5 shrink-0 text-violet-500" />
            <span>{association.adresse_asso}</span>
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {phone ? (
          <a
            href={`tel:${phone}`}
            title="Téléphoner"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 transition hover:bg-emerald-200"
          >
            <FaPhoneAlt />
          </a>
        ) : null}

        {association.mail_asso ? (
          <a
            href={`mailto:${association.mail_asso}`}
            title="Envoyer un email"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700 transition hover:bg-blue-200"
          >
            <FaEnvelope />
          </a>
        ) : null}

        {website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            title="Ouvrir le site"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700 transition hover:bg-violet-200"
          >
            <FaGlobe />
          </a>
        ) : null}

        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700"
        >
          <FaMapMarkerAlt />
          Google Maps
        </a>
      </div>
    </div>
  );
}

function AssociationMapCard({ association, selected, onSelect }) {
  const reminder = getReminderState(association.date_relance_asso);

  return (
    <button
      type="button"
      onClick={() => onSelect(association.id)}
      className={[
        "w-full rounded-xl border p-3 text-left transition",
        selected
          ? "border-violet-400 bg-violet-50 ring-4 ring-violet-500/10 dark:border-violet-500 dark:bg-violet-500/10"
          : "border-slate-200 bg-white hover:border-violet-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-violet-500/40 dark:hover:bg-slate-800",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700">
          <img
            src={association.logo_asso || fallbackLogo}
            alt=""
            className="h-full w-full object-contain"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = fallbackLogo;
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-sm font-semibold text-slate-900 dark:text-white">
            {association.nom_asso || "—"}
          </div>

          <div className="mt-1">
            <AssociationStatusBadge status={association.statut_asso} />
          </div>
        </div>

        {association.is_favorite ? (
          <FaStar className="shrink-0 text-amber-400" />
        ) : null}
      </div>

      {association.adresse_asso ? (
        <div className="mt-2 line-clamp-2 flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
          <FaMapMarkerAlt className="mt-0.5 shrink-0" />
          <span>{association.adresse_asso}</span>
        </div>
      ) : null}

      {association.date_relance_asso ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-500">
            Relance {formatAssociationDate(association.date_relance_asso)}
          </span>

          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${reminder.tone}`}
          >
            {reminder.label}
          </span>
        </div>
      ) : null}
    </button>
  );
}

function MapLegend({ entries }) {
  if (!entries.length) return null;

  return (
    <div className="absolute bottom-4 left-4 z-[500] hidden max-h-56 w-64 overflow-y-auto rounded-xl border border-white/60 bg-white/95 p-3 shadow-xl backdrop-blur xl:block">
      <div className="mb-2 text-xs font-semibold text-slate-900">
        Statuts affichés
      </div>

      <div className="space-y-1.5">
        {entries.map((entry) => (
          <div
            key={entry.status}
            className="flex items-center justify-between gap-2 text-[11px]"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{
                  backgroundColor: entry.chartColor,
                }}
              />

              <span className="truncate text-slate-600" title={entry.status}>
                {entry.status}
              </span>
            </div>

            <span className="font-semibold text-slate-900">{entry.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CartoClients() {
  useScrollToTop();

  const [associations, setAssociations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [selectedAssociationId, setSelectedAssociationId] = useState(null);

  const loadAssociations = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const data = await getAllAssociations();

      setAssociations(Array.isArray(data) ? data : []);
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssociations();
  }, [loadAssociations]);

  const setFilter = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSelectedAssociationId(null);
  };

  const productOptions = useMemo(() => {
    return [
      ...new Set(
        associations
          .map((association) => association.produit_asso?.trim())
          .filter(Boolean),
      ),
    ].sort((first, second) => first.localeCompare(second, "fr"));
  }, [associations]);

  const disciplineOptions = useMemo(() => {
    const disciplines = new Set();

    associations.forEach((association) => {
      splitDisciplines(association.discipline_asso).forEach((discipline) => {
        disciplines.add(discipline);
      });
    });

    return [...disciplines].sort((first, second) =>
      first.localeCompare(second, "fr"),
    );
  }, [associations]);

  const matchingAssociations = useMemo(() => {
    const search = normalizeSearchText(filters.search);

    const selectedDiscipline = normalizeSearchText(filters.discipline);

    return associations.filter((association) => {
      if (search && !getAssociationSearchText(association).includes(search)) {
        return false;
      }

      if (filters.status && association.statut_asso !== filters.status) {
        return false;
      }

      if (filters.product && association.produit_asso !== filters.product) {
        return false;
      }

      if (selectedDiscipline) {
        const disciplines = splitDisciplines(association.discipline_asso).map(
          normalizeSearchText,
        );

        if (!disciplines.includes(selectedDiscipline)) {
          return false;
        }
      }

      if (filters.favoriteOnly && !association.is_favorite) {
        return false;
      }

      return true;
    });
  }, [associations, filters]);

  const mappedAssociations = useMemo(() => {
    return matchingAssociations
      .filter(hasValidCoordinates)
      .sort((first, second) =>
        String(first.nom_asso || "").localeCompare(
          String(second.nom_asso || ""),
          "fr",
        ),
      );
  }, [matchingAssociations]);

  const missingCoordinatesCount =
    matchingAssociations.length - mappedAssociations.length;

  const activeFilterCount = [
    filters.search,
    filters.status,
    filters.product,
    filters.discipline,
    filters.favoriteOnly,
  ].filter(Boolean).length;

  const legendEntries = useMemo(() => {
    const counts = new Map();

    mappedAssociations.forEach((association) => {
      const status = association.statut_asso || "Statut inconnu";

      counts.set(status, (counts.get(status) || 0) + 1);
    });

    return [...counts.entries()]
      .map(([status, count]) => {
        const meta = getAssociationStatusMeta(status);

        return {
          status,
          count,
          chartColor: meta.chartColor,
        };
      })
      .sort((first, second) => {
        const firstIndex = ASSOCIATION_STATUS_INDEX[first.status] ?? 999;

        const secondIndex = ASSOCIATION_STATUS_INDEX[second.status] ?? 999;

        return firstIndex - secondIndex;
      });
  }, [mappedAssociations]);

  useEffect(() => {
    if (!selectedAssociationId) return;

    const selectionStillVisible = mappedAssociations.some(
      (association) => association.id === selectedAssociationId,
    );

    if (!selectionStillVisible) {
      setSelectedAssociationId(null);
    }
  }, [mappedAssociations, selectedAssociationId]);

  return (
    <div className="space-y-5 pb-8">
      <header className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-violet-600 to-fuchsia-600 p-5 text-white shadow-xl shadow-violet-950/10 sm:p-7">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium">
              <FaMapMarkedAlt />
              Cartographie CRM
            </div>

            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Cartographie des associations
            </h1>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/80">
              Localisez vos prospects, clients et partenaires, puis filtrez la
              carte selon leur statut et leur activité.
            </p>
          </div>

          <button
            type="button"
            onClick={loadAssociations}
            disabled={loading}
            className="inline-flex w-fit items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20 disabled:opacity-60"
          >
            <FaSync className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>

        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur">
            <div className="text-xs text-white/70">Total des dossiers</div>
            <div className="mt-1 text-2xl font-bold">{associations.length}</div>
          </div>

          <div className="rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur">
            <div className="text-xs text-white/70">Affichés sur la carte</div>
            <div className="mt-1 text-2xl font-bold">
              {mappedAssociations.length}
            </div>
          </div>

          <div className="col-span-2 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur sm:col-span-1">
            <div className="text-xs text-white/70">Sans coordonnées GPS</div>
            <div className="mt-1 text-2xl font-bold">
              {missingCoordinatesCount}
            </div>
          </div>
        </div>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
              <FaFilter className="text-violet-500" />
              Filtres de la carte
              {activeFilterCount > 0 ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-bold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                  {activeFilterCount}
                </span>
              ) : null}
            </div>

            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {mappedAssociations.length} association
              {mappedAssociations.length > 1 ? "s" : ""} visible
              {mappedAssociations.length > 1 ? "s" : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={resetFilters}
            disabled={!activeFilterCount}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Réinitialiser
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <label className="sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
              Recherche
            </span>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="search"
                value={filters.search}
                onChange={(event) => setFilter("search", event.target.value)}
                placeholder="Nom, adresse, ville, email…"
                className={`${SELECT_CLASS} pl-10`}
              />
            </div>
          </label>

          <FilterSelect
            label="Statut"
            value={filters.status}
            onChange={(value) => setFilter("status", value)}
          >
            <option value="">Tous les statuts</option>

            {ASSOCIATION_STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Produit"
            value={filters.product}
            onChange={(value) => setFilter("product", value)}
          >
            <option value="">Tous les produits</option>

            {productOptions.map((product) => (
              <option key={product} value={product}>
                {product}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Discipline"
            value={filters.discipline}
            onChange={(value) => setFilter("discipline", value)}
          >
            <option value="">Toutes les disciplines</option>

            {disciplineOptions.map((discipline) => (
              <option key={discipline} value={discipline}>
                {discipline}
              </option>
            ))}
          </FilterSelect>
        </div>

        <label className="mt-4 flex w-fit cursor-pointer items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-500/20 dark:bg-amber-500/10">
          <input
            type="checkbox"
            checked={filters.favoriteOnly}
            onChange={(event) =>
              setFilter("favoriteOnly", event.target.checked)
            }
            className="h-4 w-4 accent-amber-500"
          />

          <span className="flex items-center gap-2 text-xs font-medium text-amber-800 dark:text-amber-200">
            <FaStar />
            Afficher uniquement les favoris
          </span>
        </label>
      </section>

      {loadError ? (
        <section className="flex flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-5 py-12 text-center dark:border-red-500/20 dark:bg-red-500/10">
          <FaExclamationTriangle className="text-3xl text-red-500" />

          <h2 className="mt-3 font-semibold text-red-800 dark:text-red-200">
            Impossible de charger la carte
          </h2>

          <p className="mt-1 text-sm text-red-600 dark:text-red-300">
            {getAssociationApiError(
              loadError,
              "Le chargement des associations a échoué.",
            )}
          </p>

          <button
            type="button"
            onClick={loadAssociations}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white"
          >
            <FaSync />
            Réessayer
          </button>
        </section>
      ) : (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="order-2 flex max-h-80 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:order-1 lg:max-h-[70vh]">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="font-semibold text-slate-900 dark:text-white">
                Associations visibles
              </div>

              <div className="mt-0.5 text-xs text-slate-500">
                Sélectionnez un dossier pour centrer la carte.
              </div>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-28 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800"
                  />
                ))
              ) : mappedAssociations.length ? (
                mappedAssociations.map((association) => (
                  <AssociationMapCard
                    key={association.id}
                    association={association}
                    selected={selectedAssociationId === association.id}
                    onSelect={setSelectedAssociationId}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center px-3 py-10 text-center">
                  <FaBuilding className="text-3xl text-slate-300" />

                  <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                    Aucun résultat cartographié
                  </div>

                  <div className="mt-1 text-xs text-slate-400">
                    Modifiez les filtres ou renseignez les coordonnées GPS des
                    associations.
                  </div>
                </div>
              )}
            </div>

            {missingCoordinatesCount > 0 ? (
              <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                {missingCoordinatesCount} dossier
                {missingCoordinatesCount > 1 ? "s" : ""} correspondant aux
                filtres ne possède
                {missingCoordinatesCount > 1 ? "nt" : ""} pas encore de
                coordonnées GPS.
              </div>
            ) : null}
          </aside>

          <div className="relative order-1 h-[62vh] min-h-[480px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 lg:order-2 lg:h-[70vh]">
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              scrollWheelZoom
              className="h-full w-full"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              <MapViewportController
                associations={mappedAssociations}
                selectedAssociationId={selectedAssociationId}
              />

              {mappedAssociations.map((association) => (
                <Marker
                  key={association.id}
                  position={[
                    Number(association.latitude),
                    Number(association.longitude),
                  ]}
                  icon={getStatusMarkerIcon(association.statut_asso)}
                  zIndexOffset={
                    selectedAssociationId === association.id ? 1000 : 0
                  }
                  eventHandlers={{
                    click: () => setSelectedAssociationId(association.id),
                  }}
                >
                  <Popup minWidth={260} maxWidth={310}>
                    <AssociationPopup association={association} />
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            <div className="pointer-events-none absolute right-4 top-4 z-[500] rounded-xl border border-white/60 bg-white/95 px-3 py-2 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur">
              <FaMapMarkerAlt className="mr-1.5 inline text-violet-500" />
              {mappedAssociations.length} point
              {mappedAssociations.length > 1 ? "s" : ""}
            </div>

            <MapLegend entries={legendEntries} />
          </div>
        </section>
      )}

      <style>{`
        .association-marker-wrapper {
          border: 0;
          background: transparent;
        }

        .association-map-marker {
          position: relative;
          display: block;
          width: 30px;
          height: 30px;
          border: 3px solid white;
          border-radius: 50% 50% 50% 0;
          background: var(--association-marker-color);
          box-shadow:
            0 5px 12px rgba(15, 23, 42, 0.3),
            0 0 0 1px rgba(15, 23, 42, 0.08);
          transform: rotate(-45deg);
        }

        .association-map-marker-dot {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: white;
          transform:
            translate(-50%, -50%)
            rotate(45deg);
        }

        .leaflet-popup-content-wrapper {
          border-radius: 16px;
        }

        .leaflet-popup-content {
          margin: 14px;
        }
      `}</style>
    </div>
  );
}
